import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useUnmount } from 'react-use';

import { NotFound, ProductDetailRenderer } from '../../../containers';
import { MeilisearchAttribute, meilisearchVariantsIndex } from '../../../entities/@api/Meilisearch';
import { GTMProduct } from '../../../entities/@api/Sylius/Resource/GTM';
import { transformToProduct } from '../../../entities/@products/Product/ProductTransformers';
import { MeilisearchStoreKey } from '../../../entities/@search/Meilisearch/Meilisearch';
import { generateMeilisearchExcludeFilterQuery, generateMeilisearchFilterQuery } from '../../../entities/@search/Meilisearch/MeilisearchRequests';
import { GTMEvent } from '../../../entities/GTM/GTM';
import { generateGTMProduct } from '../../../entities/GTM/GTMRequests';
import { sendGTMEvent } from '../../../entities/GTM/GTMService';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { combineBaseFilterWithFilterQuery } from '../../../helpers/meilisearch';
import { replaceUrlParamKeysWithValues } from '../../../helpers/url';
import { useClientEffect, useTrans } from '../../../hooks';
import { fetchBrand } from '../../../redux/@brands/brand/brandActions';
import { setBrand } from '../../../redux/@brands/brand/brandSlice';
import { fetchBrands } from '../../../redux/@brands/brands/brandsActions';
import { fetchProductFromRelativeUrl } from '../../../redux/@products/product/productActions';
import { setProductDefault, setProductVariant } from '../../../redux/@products/product/productSlice';
import { addProductVariantToCart } from '../../../redux/@shop/cart/cartActions';
import { addProductVariantToWishlist, removeProductVariantFromWishlist } from '../../../redux/@shop/wishlist/wishlistActions';
import { fetchMeilisearchVariants } from '../../../redux/meilisearch/meilisearchActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

interface ConnectedProductDetailRendererProps {
    className?: string;
}

export const ConnectedProductDetailRenderer: FC<ConnectedProductDetailRendererProps> = ({
    className = '',
}): ReactElement => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const trans = useTrans();

    const dispatch = useTypedDispatch();

    const isLoadingProductDefault = useTypedSelector(state => state.productSlice.isLoadingProductDefault);
    const hasFetched = useTypedSelector(state => state.productSlice.hasFetched);
    const breadcrumbs = useTypedSelector(state => state.productSlice.breadcrumbs);
    const productDefault = useTypedSelector(state => state.productSlice.productDefault);
    const productVariant = useTypedSelector(state => state.productSlice.productVariant);

    const brand = useTypedSelector(state => state.brandSlice.brand);
    const brands = useTypedSelector(state => state.brandsSlice.brands);

    const cartIsLoading = useTypedSelector(state => state.cartSlice.isLoading);
    const cart = useTypedSelector(state => state.cartSlice.cart);

    const wishlistIsLoading = useTypedSelector(state => state.wishlistSlice.isLoading);

    const variantResponses = useTypedSelector(state => state.meilisearchSlice.variantResponses);

    const brandStoreKey = MeilisearchStoreKey.productDetailBrand;
    const relatedStoreKey = MeilisearchStoreKey.productDetailRelated;

    const inCartQuantity = useMemo((): number => {
        const foundItem = cart.items.find(item => item.variantCode === productVariant?.variantCode);

        return foundItem?.quantity || 0;
    }, [cart, productDefault]);

    const wishlist = useTypedSelector(state => state.wishlistSlice.wishlist);

    const [isInWishlist, setIsInWishlist] = useState<boolean>(false);

    // Remove product data from store on unmount
    useUnmount((): void => {
        dispatch(setProductDefault(undefined));
        dispatch(setProductVariant(undefined));
    });

    // Redirect to correct relative URL from product variant when it's incorrect
    useEffect((): void => {
        if (!productVariant) {
            return;
        }

        if (productVariant.relativeUrl !== slug) {
            const productDetailPath = trans(appRoutes[AppRoute.productDetail].path);
            const productDetailUrl = replaceUrlParamKeysWithValues(productDetailPath, {
                slug: productVariant.relativeUrl,
            });

            navigate(productDetailUrl, { replace: true });
        }
    }, [productVariant]);

    // Fetch data for page
    useClientEffect((): void => {
        if (!slug) {
            return;
        }

        dispatch(fetchProductFromRelativeUrl(slug));
        dispatch(fetchBrands());
    }, [slug]);

    // Fetch brand based on data from product variant
    useClientEffect((): void => {
        if (!productVariant || brands.length === 0) {
            return;
        }

        if (!productVariant?.brand) {
            dispatch(setBrand(undefined));
            return;
        }

        const productBrand = brands.find(brandItem => brandItem.name === productVariant.brand?.label);

        if (productBrand) {
            dispatch(fetchBrand(productBrand.slug));
        }
    }, [productVariant, brands]);

    // Fetch Meilisearch data based on product data
    useEffect((): void => {
        if (!productDefault || !productVariant) {
            return;
        }

        const transformedProduct = transformToProduct(productDefault, productVariant, productDefault.categories || []);
        const gtmProduct = generateGTMProduct(transformedProduct);

        sendGTMEvent(GTMEvent.viewItem, gtmProduct);

        const excludeFilter = generateMeilisearchExcludeFilterQuery(MeilisearchAttribute.code, productVariant.variantCode);

        const relatedBaseFilter = generateMeilisearchFilterQuery(MeilisearchAttribute.categories, productDefault.categories || []);
        const relatedFilter = combineBaseFilterWithFilterQuery(relatedBaseFilter, excludeFilter);

        dispatch(fetchMeilisearchVariants({
            key: relatedStoreKey,
            index: meilisearchVariantsIndex,
            pageSize: 12,
            filter: relatedFilter,
        }));

        if (!productVariant.brand) {
            dispatch(setBrand(undefined));
            return;
        }

        const brandBaseFilter = generateMeilisearchFilterQuery(MeilisearchAttribute.brand, productVariant.brand.label);
        const brandFilter = combineBaseFilterWithFilterQuery(brandBaseFilter, excludeFilter);

        dispatch(fetchMeilisearchVariants({
            key: brandStoreKey,
            index: meilisearchVariantsIndex,
            pageSize: 12,
            filter: brandFilter,
        }));
    }, [productDefault]);

    // Update interface based on whether product variant exists in wishlist
    useEffect((): void => {
        if (!productVariant) {
            return;
        }

        const isProductVariantInWishlist = wishlist.variantIds.includes(productVariant.variantId);

        setIsInWishlist(isProductVariantInWishlist);
    }, [wishlist]);

    const gtmProduct = useMemo((): GTMProduct | undefined => {
        if (!productDefault || !productVariant) {
            return undefined;
        }

        const transformedProduct = transformToProduct(productDefault, productVariant, productDefault.categories || []);
        return generateGTMProduct(transformedProduct);
    }, [productDefault, productVariant]);

    const handleWishlistButtonClick = async (): Promise<void> => {
        if (!productVariant) {
            return;
        }

        if (isInWishlist) {
            await dispatch(removeProductVariantFromWishlist(productVariant.variantId));
            return;
        }

        await dispatch(addProductVariantToWishlist(productVariant.variantId));

        sendGTMEvent(GTMEvent.addToWishlist, gtmProduct);
    };

    const handleAddToCart = async (): Promise<void> => {
        if (!productVariant) {
            return;
        }

        await dispatch(addProductVariantToCart(productVariant.variantCode));

        sendGTMEvent(GTMEvent.addToCart, gtmProduct);
    };

    if (hasFetched && !productVariant) {
        return (
            <NotFound />
        );
    }

    return (
        <ProductDetailRenderer
            productDefaultIsLoading={isLoadingProductDefault}
            cartIsLoading={cartIsLoading}
            wishlistIsLoading={wishlistIsLoading}
            isInWishlist={isInWishlist}
            productDefault={productDefault}
            productVariant={productVariant}
            inCartQuantity={inCartQuantity}
            brand={brand}
            stockQuantity={productVariant?.stock || 0}
            breadcrumbs={breadcrumbs}
            brandProducts={variantResponses?.[brandStoreKey]?.results || []}
            relatedProducts={variantResponses?.[relatedStoreKey]?.results || []}
            onAddToCart={handleAddToCart}
            onWishlistButtonClick={handleWishlistButtonClick}
            className={className}
        />
    );
};
